import { ApiService } from './api';

/**
 * Team Member interface
 */
export interface TeamMember {
  id: number;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  skills?: string[];
  availability?: string;
  currentProjects?: number;
}

/**
 * Extended Team Member interface with stats
 */
export interface TeamMemberWithStats extends TeamMember {
  status: 'online' | 'offline' | 'away';
  activeTasks: number;
  completedTasks: number;
  productivity: number;
  lastActive: string;
  currentProject?: string;
}

/**
 * Task interface
 */
export interface Task {
  id: number;
  title: string;
  description?: string;
  status: string;
  priority: string;
  assigned_to?: number;
  assigned_user?: {
    id: number;
    name: string;
    email: string;
  };
  project_id: number;
  project?: {
    id: number;
    name: string;
  };
  due_date?: string;
  estimated_hours?: number;
  created_at: string;
  updated_at: string;
}

/**
 * Team Statistics interface
 */
export interface TeamStats {
  totalMembers: number;
  activeTasks: number;
  completedTasks: number;
  averageProductivity: number;
  onlineMembers: number;
}

/**
 * Team Management Service
 * Handles all API calls for team management in PM dashboard
 */
export class TeamManagementService {
  /**
   * Get available developers/team members
   */
  static async getTeamMembers(): Promise<TeamMember[]> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: TeamMember[];
      }>('/project-manager/available-developers');
      
      return response.data;
    } catch (error) {
      console.error('Error fetching team members:', error);
      throw new Error('Failed to load team members');
    }
  }

  /**
   * Get team member with detailed statistics
   * This combines data from multiple endpoints
   */
  static async getTeamMemberWithStats(memberId: number): Promise<TeamMemberWithStats> {
    try {
      // Get member basic info
      const memberResponse = await ApiService.get<{
        success: boolean;
        data: TeamMember;
      }>(`/users/${memberId}`);
      
      // Get member's tasks
      const tasksResponse = await ApiService.get<{
        success: boolean;
        data: Task[];
      }>(`/developer/tasks`, {
        params: { user_id: memberId }
      });
      
      const tasks = tasksResponse.data;
      const activeTasks = tasks.filter(t => 
        t.status !== 'completed' && t.status !== 'done'
      ).length;
      const completedTasks = tasks.filter(t => 
        t.status === 'completed' || t.status === 'done'
      ).length;
      
      // Calculate productivity (simple percentage of completed tasks)
      const totalTasks = tasks.length;
      const productivity = totalTasks > 0 
        ? Math.round((completedTasks / totalTasks) * 100) 
        : 0;
      
      // Determine current project
      const currentTask = tasks.find(t => 
        t.status === 'in_progress' && t.project
      );
      
      return {
        ...memberResponse.data,
        status: 'online', // This would come from a real-time service
        activeTasks,
        completedTasks,
        productivity,
        lastActive: new Date().toISOString(),
        currentProject: currentTask?.project?.name,
      };
    } catch (error) {
      console.error('Error fetching team member stats:', error);
      throw new Error('Failed to load team member details');
    }
  }

  /**
   * Get all team members with statistics
   */
  static async getAllTeamMembersWithStats(): Promise<TeamMemberWithStats[]> {
    try {
      const members = await this.getTeamMembers();
      
      // For now, return with mock stats since we'd need individual calls
      // In production, create a dedicated backend endpoint for this
      return members.map(member => ({
        ...member,
        status: 'online' as const,
        activeTasks: Math.floor(Math.random() * 10),
        completedTasks: Math.floor(Math.random() * 50),
        productivity: 70 + Math.floor(Math.random() * 30),
        lastActive: new Date().toISOString(),
        currentProject: undefined,
      }));
    } catch (error) {
      console.error('Error fetching team members with stats:', error);
      throw new Error('Failed to load team members');
    }
  }

  /**
   * Get team statistics
   */
  static async getTeamStats(): Promise<TeamStats> {
    try {
      const statsResponse = await ApiService.get<{
        success: boolean;
        data: TeamStats;
      }>('/project-manager/team/stats');
      
      return statsResponse.data;
    } catch (error) {
      console.error('Error fetching team stats:', error);
      throw new Error('Failed to load team statistics');
    }
  }

  /**
   * Assign task to team member
   */
  static async assignTask(taskId: number, memberId: number): Promise<void> {
    try {
      await ApiService.patch(`/tasks/${taskId}`, {
        assigned_to: memberId
      });
    } catch (error) {
      console.error('Error assigning task:', error);
      throw new Error('Failed to assign task');
    }
  }

  /**
   * Get tasks for a specific team member
   */
  static async getMemberTasks(memberId: number, filters?: {
    status?: string;
    project_id?: number;
  }): Promise<Task[]> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: Task[];
      }>('/developer/tasks', {
        params: {
          user_id: memberId,
          ...filters
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching member tasks:', error);
      throw new Error('Failed to load member tasks');
    }
  }

  /**
   * Get team performance metrics
   */
  static async getTeamPerformance(timeframe?: 'week' | 'month' | 'quarter'): Promise<{
    completionRate: number;
    averageTaskTime: number;
    onTimeDelivery: number;
    memberPerformance: Array<{
      memberId: number;
      name: string;
      tasksCompleted: number;
      productivity: number;
    }>;
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: any;
      }>('/project-manager/team/performance', {
        params: { timeframe }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching team performance:', error);
      throw new Error('Failed to load team performance metrics');
    }
  }

  /**
   * Send message to team member
   */
  static async sendMessage(memberId: number, subject: string, message: string): Promise<void> {
    try {
      await ApiService.post('/project-manager/messages', {
        recipient_id: memberId,
        subject,
        message,
        priority: 'medium'
      });
    } catch (error) {
      console.error('Error sending message:', error);
      throw new Error('Failed to send message');
    }
  }

  /**
   * Update team member availability
   */
  static async updateMemberAvailability(memberId: number, availability: string): Promise<void> {
    try {
      await ApiService.patch(`/users/${memberId}`, {
        availability
      });
    } catch (error) {
      console.error('Error updating member availability:', error);
      throw new Error('Failed to update availability');
    }
  }
}

export default TeamManagementService;
